import { Box, Button, Icon, Modal, ModalProps, TextLink } from '@palmetto/palmetto-components';
import { FormikValues, useFormikContext } from 'formik';
import { useCallback } from 'react';

import { MonitoringAccessResult } from 'types';

const notDismissAllowed: () => void = () => {};

export function MonitoringAccessConfirmationModal({
  isOpen,
  onDismiss,
  monitoringAccessConfirmationInfo,
  handleSubmit,
  isLoading,
}: ModalProps & {
  monitoringAccessConfirmationInfo?: MonitoringAccessResult[];
  handleSubmit: (values: FormikValues, { setSubmitting }: any) => Promise<void>;
  isLoading: boolean;
}) {
  const { values, isSubmitting, setSubmitting } = useFormikContext();

  const handleContinueClick = useCallback(() => {
    handleSubmit(values as FormikValues, { setSubmitting });
    setSubmitting(true);
  }, [handleSubmit, setSubmitting, values]);

  return (
    <Modal isOpen={isOpen} onDismiss={notDismissAllowed} maxWidth="4xl" ariaLabel="monitoring access confirmation">
      <Box background="grey-100">
        <Modal.Header
          title={
            (
              <>
                <Box
                  direction="row"
                  gap="md"
                  alignItems="center"
                  data-testid="monitoring-access-confirmation-modal-header"
                >
                  <Box
                    width="42px"
                    height="42px"
                    radius="circle"
                    background="grey-300"
                    borderWidth="2px"
                    borderColor="grey-600"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box
                      width="27px"
                      height="27px"
                      radius="circle"
                      background="grey-300"
                      borderWidth="md"
                      borderColor="grey-600"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon name="exclamation-mark" size="sm" color="grey-600" onClick={onDismiss} />
                    </Box>
                  </Box>

                  <Box color="grey-700" fontWeight="bold">
                    Monitoring Access Required for M1
                  </Box>
                </Box>
              </>
            ) as unknown as string
          }
          id="monitoring-access-confirmation-modal-header"
        />
      </Box>
      <Modal.Body childGap="lg">
        <Box color="black" data-testid="monitoring-access-confirmation-modal-body" direction={'column'} gap="md">
          <Box display="block" fontSize="sm">
            Our system was unable to verify monitoring access for this site. We need this access to make sure the
            hardware installed matches the design.
          </Box>
          {monitoringAccessConfirmationInfo && monitoringAccessConfirmationInfo?.length > 0 && (
            <Box background="grey-50" padding="md" radius="md" direction="column" gap="md">
              <Box fontSize="sm" color="grey-600" fontWeight="bold" display="block" marginBottom="sm">
                UNVERIFIED SYSTEMS
              </Box>
              <Box as="span" color="grey-700" fontSize="sm">
                {monitoringAccessConfirmationInfo?.map((info) => {
                  const { inverter, battery } = info.readableInverterOrBatteryInfo ?? {};
                  return (
                    <Box key={info.monitoringId} direction="column" gap="xs">
                      {inverter && (
                        <Box direction="row" gap="xs" margin="xs 0 0 0">
                          <Box>
                            <Icon name="remove-light" size="sm" color="grey-500" />
                          </Box>
                          <Box>{inverter}</Box>
                        </Box>
                      )}
                      {battery && (
                        <Box direction="row" gap="xs" margin="xs 0 0 0">
                          <Box>
                            <Icon name="remove-light" size="sm" color="grey-500" />
                          </Box>
                          <Box>{battery}</Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
          <Box display="block" fontSize="sm">
            Granting site access now helps improve your approval rate. Please take a moment to double check the site
            ID, and ensure monitoring access was granted.
          </Box>
          <Box direction="row" gap="xs">
            <Icon name="launch-app" size="sm" color="grey-600" marginRight="xs" />
            <TextLink
              href="https://help.palmetto.finance/en/articles/11705727-solar-energy-plan-monitoring-portal-deliverables-m1"
              target="_blank"
            >
              <Box fontSize="sm" fontWeight="bold" color="info">
                Review Monitoring Portal Deliverables Requirements
              </Box>
            </TextLink>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer borderWidth="none">
        <Button
          variant="secondary"
          tone="neutral"
          onClick={onDismiss}
          id="monitoringAccessConfirmationModalCancelCTA"
          isLoading={isSubmitting || isLoading}
          disabled={isSubmitting || isLoading}
        >
          <Box direction="row" alignItems="center" gap="xs">
            <Icon name="caret-sm-left" size="md" marginRight="xs" /> <Box fontWeight="bold">Go Back & Fix</Box>
          </Box>
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={handleContinueClick}
          isLoading={isSubmitting || isLoading}
          disabled={isSubmitting || isLoading}
          id="monitoringAccessConfirmationModalContinueCTA"
        >
          Submit Anyway
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
